import banner from "../../../courses/module-1/img1.png";

const modulo1 = {
  courseId: "finanzas-personales",
  id: "modulo-1",
  title: "📘 MODULE 1: Introduction to Personal Finance",
  estimatedDuration: "1 hour",
  format: "Reading + reflective activity + assessment",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objectives",
      title: "🎯 Module objectives",
      content: [
        "Understand what personal finance is and why it is key in adult life.",
        "Question cultural myths that limit our relationship with money.",
        "Inspire you to start managing your money now, with real tools and relatable examples.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Main reading",
      title: "📖 Main reading: What is personal finance?",
      content: [
        {
          type: "paragraph",
          text: "Personal finance includes all the decisions and actions you take regarding your money. It is not about huge amounts or being “good with numbers.” It is simply about answering everyday questions like:",
        },
        {
          type: "list",
          items: [
            "What do I spend my paycheck on?",
            "How much do I need to cover my fixed expenses?",
            "How can I start saving for something important?",
            "Can I afford the graduation trip?",
            "How do I avoid getting into credit card debt?",
          ],
        },
        {
          type: "paragraph",
          text: "Relatable example: María, 18, lives in Guadalajara and studies at UDG. She receives a maintenance scholarship and works part-time at a café. She started by writing down all her daily expenses in a free app. She discovered she was spending $350 pesos a month on snacks. She changed that habit and now saves that same amount to buy a new phone.",
        },
      ],
    },
    {
      id: "importancia",
      navLabel: "Importance",
      title: "🧠 Why is it so important to learn this?",
      content: [
        {
          type: "paragraph",
          text: "Because if you do not decide how to use your money, someone else will: advertising, social media, the algorithm, or the craving of the moment.",
        },
        {
          type: "paragraph",
          text: "Managing your finances well is not only a useful skill; it is a tool for freedom. It helps you:",
        },
        {
          type: "list",
          items: [
            "Avoid financial stress",
            "Make decisions without fear",
            "Save for what you truly want",
            "Live with more clarity and independence",
          ],
        },
      ],
    },
    {
      id: "mitos",
      navLabel: "Common myths",
      title: "🧨 Common myths about money",
      content: [
        {
          type: "orderedList",
          items: [
            `"I am too young to think about this": Actually, the earlier you start, the more advantage you have. If you start building the habit of saving at 17, by 25 you will already have a strong foundation.`,
            `"I do not earn enough to save": It is not about how much, but about starting. $10 a week → $520 a year. If you build that habit, you are already developing your financial mindset.`,
            `"Finance is only for rich people or experts": You do not need to know about the stock market or cryptocurrencies to make good decisions. Understanding the basics — income, expenses, savings, and credit — is more than enough to start.`,
          ],
        },
      ],
    },
    {
      id: "actividad",
      navLabel: "Reflective activity",
      title: "💡 Reflective activity",
      content: [
        "Take a moment to answer these questions, either on paper or on your phone:",
        {
          type: "list",
          items: [
            "What do you think of when you hear the word money?",
            "What phrase about money did you hear often at home?",
            "Have you ever felt anxious because you did not know if your money would be enough? What did you do?",
            "What would you like to change in your relationship with money starting today?",
          ],
        },
      ],
    },
    {
      id: "recurso",
      navLabel: "Additional resource",
      title: "🎬 Additional resource",
      content: [
        "To reinforce what you learned in this module, we invite you to watch the following video, which offers a step-by-step guide to personal finance for beginners. This resource will help you visualize and better understand the key concepts we covered.",
        {
          type: "link",
          href: "https://www.youtube.com/watch?v=hddYOF8nx24",
          text: "Basic financial education: What should you know before getting started?",
        },
      ],
    },
    {
      id: "lecturas",
      navLabel: "Recommended readings",
      title: "📚 Recommended readings",
      content: [
        {
          type: "link",
          href: "http://www.condusef.gob.mx/",
          text: "Finance for Young People – CONDUSEF",
        },
        {
          type: "link",
          href: "https://www.pequenocerdocapitalista.com/",
          text: "Pequeño Cerdo Capitalista Blog",
        },
      ],
    },
    {
      id: "quiz",
      navLabel: "Quiz",
      title: "📝 Self-assessment miniquiz",
      questions: [
        {
          id: "q1",
          question: "What does the term *personal finance* include?",
          options: [
            { key: "a", text: "Investing in the stock market" },
            { key: "b", text: "Only my income" },
            {
              key: "c",
              text: "Everything I do with my money: income, expenses, savings, etc.",
            },
            { key: "d", text: "Talking about money with a bank" },
          ],
          correct: "c",
        },
        {
          id: "q2",
          question: "Which one is a common myth?",
          options: [
            { key: "a", text: "That saving is key" },
            { key: "b", text: "That money is not for young people" },
            { key: "c", text: "That expenses should be controlled" },
            { key: "d", text: "That insurance exists" },
          ],
          correct: "b",
        },
        {
          id: "q3",
          question: "What can help you live with more peace of mind?",
          options: [
            { key: "a", text: "Buying without thinking" },
            { key: "b", text: "Using all your credit" },
            { key: "c", text: "Managing your personal finances" },
            { key: "d", text: "Ignoring your debts" },
          ],
          correct: "c",
        },
        {
          id: "q4",
          question: "What is recommended even if you earn little?",
          options: [
            { key: "a", text: "Investing in crypto" },
            { key: "b", text: "Saving even if it is only $10" },
            { key: "c", text: "Not talking about money" },
            { key: "d", text: "Living day to day" },
          ],
          correct: "b",
        },
        {
          id: "q5",
          question: "Why is it important to start young?",
          options: [
            { key: "a", text: "Because they force you" },
            { key: "b", text: "Because it is trendy" },
            { key: "c", text: "Because time multiplies your money" },
            { key: "d", text: "Because everyone does it" },
          ],
          correct: "c",
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Bibliography",
      title: "📚 Bibliography for further learning",
      content: [
        "Sofía Macías – “Pequeño Cerdo Capitalista”",
        "Ramsey Solutions – “The Total Money Makeover”",
        "Luis Pita – “Ten peor coche que tu vecino”",
      ],
    },
    {
      id: "frase",
      navLabel: "Quote of the day",
      title: "🧠 Quote of the day",
      content: [
        "“Money does not define you, but knowing how to use it gives you freedom.”",
      ],
    },
  ],
};

export default modulo1;
